import Button from '@rio-cloud/rio-uikit/Button';
import { FormattedMessage, useIntl } from 'react-intl';

import { getServiceOrderIcon } from '#components/shared/orderIcons.ts';
import { ServiceTypeName } from '#components/shared/ServiceTypeName.tsx';
import { StopInfo } from '#components/shared/StopInfo.tsx';
import type { StopLoadMetrics } from '#domain/loadMetrics.ts';
import type { Order } from '#domain/Order.ts';
import type { ServiceOrderCategory } from '#domain/ServiceOrder.ts';
import { canMoveStopInTour, isChargingStop, type TourStop } from '#domain/TourStop.ts';

import { ChargingDetailsView } from './ChargingDetailsView.tsx';
import { LoadMetrics } from './LoadMetrics.tsx';
import { TourListCard } from './TourListCard.tsx';
import { formatTourStopMarkerLabel, type TourStopSequence } from './TourStopSequenceMarker.tsx';

type StopRowProps = {
    item: TourStop;
    order?: Order;
    allStops: TourStop[];
    isMisplaced: boolean;
    loadMetrics?: StopLoadMetrics;
    canEdit?: boolean;
    onEditStop?: (stop: TourStop) => void;
    sequenceMarker?: TourStopSequence;
    onRemoveStop?: (stop: TourStop) => void;
};

const stopIconClasses = (category?: ServiceOrderCategory): Record<TourStop['type'], [string, string]> => {
    return {
        execute: [getServiceOrderIcon(category), 'display-none'],
        load: ['icon-arrow-down margin-left--5', 'icon-palette margin-left--3'],
        unload: ['icon-palette margin-right--3', 'icon-arrow-up margin-right--5'],
        unknown: ['icon-palette margin-right--3', 'icon-arrow-up margin-right--5'],
    };
};

const StopIcon = ({ item }: { item: TourStop }) => {
    const [primaryIconClasses, secondaryIconClasses] = stopIconClasses(item.serviceOrderCategory)[item.type];

    return (
        <div className='display-flex flex-column align-items-center gap-2'>
            <div className='display-flex justify-content-center'>
                <span className={`text-size-20 text-color-darker uikit-icon ${primaryIconClasses}`} />
                <span className={`text-size-20 text-color-darker uikit-icon ${secondaryIconClasses}`} />
            </div>
            <span className='text-size-12'>
                {isChargingStop(item) ? (
                    <ServiceTypeName value={item.serviceOrderCategory} category={item.serviceOrderCategory} />
                ) : (
                    <FormattedMessage id={`intl-msg:ordercom-cloud-frontend.tours.stop.type.${item.type}`} />
                )}
            </span>
        </div>
    );
};

const DragHandler = () => (
    <div className='min-height-100pct display-flex align-items-center border border-left-only drag-handler cursor-grab flex-0'>
        <Button
            bsStyle='muted'
            iconOnly
            iconName='drag-n-drop'
            autoTrackingKey='ordercom.tours.create-dialog.stop-row.drag-handle'
            className='cursor-grab hover-bg-none'
        />
    </div>
);

const DragPlaceholder = () => (
    <div className='min-height-100pct display-flex align-items-center border border-left-only flex-0'>
        <Button bsStyle='muted' iconOnly iconName='drag-n-drop' className='opacity-30 hover-bg-none' disabled />
    </div>
);

const StopRowDetails = ({
    item,
    order,
    isMisplaced,
    loadMetrics,
    canEdit,
    onEditStop,
    sequenceMarker,
}: {
    item: TourStop;
    order?: Order;
    isMisplaced: boolean;
    loadMetrics?: StopLoadMetrics;
    canEdit?: boolean;
    onEditStop?: (stop: TourStop) => void;
    sequenceMarker?: TourStopSequence;
}) => {
    const intl = useIntl();

    return (
        <div className='display-flex flex-column justify-content-center min-width-0' data-testid='stop-row-details'>
            <div className='display-flex align-items-start gap-10' data-testid='stop-row-main-content'>
                <div className='flex-1-1-0 min-width-0'>
                    <StopInfo
                        variant={sequenceMarker ? 'summary' : 'default'}
                        name={
                            item.type === 'execute' && !item.charging ? (
                                <ServiceTypeName value={item.name} category={item.serviceOrderCategory} />
                            ) : (
                                item.name
                            )
                        }
                        location={item.location}
                        from={item.start}
                        to={item.end}
                        canEdit={canEdit}
                        orderReference={
                            sequenceMarker && item.type !== 'execute' ? (item.orderNumber ?? item.customer) : undefined
                        }
                        onEdit={onEditStop ? () => onEditStop(item) : undefined}
                        stopStatus={item.status}
                        stopType={item.type}
                        orderStatus={order?.status}
                        showStatusInfo
                    />
                </div>
                <div
                    className='flex-0 display-flex flex-column align-items-center gap-5'
                    data-testid='stop-row-order-badge'
                >
                    {!sequenceMarker &&
                        item.serviceOrderCategory !== 'tour-start' &&
                        item.serviceOrderCategory !== 'tour-end' && (
                            <span className={`label label-${getLabel(item.group)}`}>
                                {item.type === 'execute' ? (
                                    <ServiceTypeName
                                        value={item.orderNumber ?? item.name}
                                        category={item.serviceOrderCategory}
                                    />
                                ) : (
                                    (item.orderNumber ?? item.customer)
                                )}
                            </span>
                        )}
                    {sequenceMarker &&
                        (item.serviceOrderCategory === 'tour-start' || item.serviceOrderCategory === 'tour-end' ? (
                            <span
                                className='label label-info text-nowrap'
                                role='img'
                                aria-label={intl.formatMessage({
                                    id: `intl-msg:ordercom-cloud-frontend.service.type.${item.serviceOrderCategory}`,
                                })}
                                title={item.orderNumber ?? item.customer}
                            >
                                <ServiceTypeName value={item.name} category={item.serviceOrderCategory} />
                            </span>
                        ) : (
                            <span
                                className='label label-info text-nowrap'
                                role='img'
                                aria-label={formatTourStopMarkerLabel(intl, sequenceMarker)}
                                title={item.orderNumber ?? item.customer}
                            >
                                #{sequenceMarker.sequence}
                            </span>
                        ))}
                </div>
            </div>
            {item.charging && <ChargingDetailsView value={item.charging} />}
            {loadMetrics && (
                <LoadMetrics metrics={loadMetrics} stopType={item.type} summary={Boolean(sequenceMarker)} />
            )}
            {isMisplaced && (
                <span className='position-absolute bottom--1 right-15 bg-danger padding-x-10 padding-y-3 rounded-top text-size-12 pointer-events-none'>
                    <FormattedMessage
                        id={
                            item.serviceOrderCategory === 'tour-start'
                                ? 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.validation.tour-start'
                                : item.serviceOrderCategory === 'tour-end'
                                  ? 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.validation.tour-end'
                                  : 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.validation.stop-order'
                        }
                    />
                </span>
            )}
        </div>
    );
};

export const StopRow = ({
    item,
    order,
    allStops,
    isMisplaced,
    loadMetrics,
    canEdit,
    onEditStop,
    sequenceMarker,
    onRemoveStop,
}: StopRowProps) => {
    const intl = useIntl();
    return (
        <div className='display-flex justify-content-between align-items-center gap-5 min-width-0'>
            <TourListCard
                leadingWidth={sequenceMarker ? 80 : 70}
                leading={<StopIcon item={item} />}
                trailing={
                    item.chargingDraftId ? (
                        <div className='border border-left-only display-flex align-items-center'>
                            <Button
                                bsStyle='muted'
                                iconOnly
                                iconName='trash'
                                aria-label={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.routing-settings.remove',
                                })}
                                onClick={() => onRemoveStop?.(item)}
                            />
                        </div>
                    ) : canMoveStopInTour(allStops, item, order) ? (
                        <DragHandler />
                    ) : (
                        <DragPlaceholder />
                    )
                }
                panelStyle={
                    isMisplaced || (!sequenceMarker && Boolean(loadMetrics?.exceededCapacity?.length))
                        ? 'danger'
                        : 'default'
                }
                shadow
                testId='tour-stop-card'
            >
                <StopRowDetails
                    item={item}
                    order={order}
                    isMisplaced={isMisplaced}
                    loadMetrics={loadMetrics}
                    canEdit={canEdit}
                    onEditStop={onEditStop}
                    sequenceMarker={sequenceMarker}
                />
            </TourListCard>
        </div>
    );
};

const getLabel = (group: number): string => {
    const labels = ['primary', 'secondary', 'info', 'success', 'warning'];
    return labels[group % labels.length];
};
